import React, {ComponentType, ReactElement} from 'react';
import {EdapProvider} from '@homeaway/react-event-tracking';
import {ErrorReportBoundary} from '@homeaway/react-error-reporter';
import FilterBar from '../FilterBar/FilterBar';
import MoreFiltersModal from '../MoreFiltersModal/MoreFiltersModal';

// The types in this file are listed here rather than being imported from other files
// This is intentional and necessary in order to generate documentation using `npm run start:docs`
// Per react-docgen (https://github.com/reactjs/react-docgen):
// "react-docgen will not be able to grab the type definition if the type is imported or declared in a different file."

type ProviderComponent = {
    searchResultsLoading: boolean;
};

// Duplicated from FilterBar
type Filter = {
    description?: string;
    refineByQueryArgument: string;
    id: string;
    groupId?: string;
    name: string;
};

// Duplicated from FilterBar
type Refinement = {
    count?: number;
    checked: boolean;
    filter: Filter;
};

// Duplicated from FilterBar
type FilterGroupInfo = {
    id: string;
    name: string;
};

// Duplicated from FilterBar
type FilterGroupType = {
    groupInfo: FilterGroupInfo;
    filters: Refinement[];
};

// Duplicated from FilterBar
type CustomFilterParams = {
    selectedFilterByTotalPrice: boolean;
    appliedFilterByTotalPrice: boolean;
    appliedMaxNightlyPrice: number | null;
    appliedMinNightlyPrice: number | null;
    selectedMaxNightlyPrice: number | null;
    selectedMinNightlyPrice: number | null;
    selectedMaxTotalPrice: number | null;
    selectedMinTotalPrice: number | null;
    appliedMaxTotalPrice: number | null;
    appliedMinTotalPrice: number | null;
    selectedMinBedrooms: number;
    selectedMinBathrooms: number;
    appliedMinBedrooms: number;
    appliedMinBathrooms: number;
};

type FiltersContainerProps = {
    allFiltersButtonCustomTrigger?: ({
        appliedCount: number,
        onClick: Function,
    }) => ReactElement;
    allFiltersButtonOnly: boolean;
    searchResultsLoading: boolean;
    filterCountDataProvider: ComponentType<ProviderComponent>;

    // Duplicated props fro FilterBar
    appliedFilters: string[];
    currency: string;
    customFiltersParams: CustomFilterParams;
    displayMoreFiltersPillLeft?: boolean;
    displayNumGroupsAsPills?: number;
    filterGroups: FilterGroupType[];
    isMobile: boolean;
    onClear: Function;
    onCustomFilterUpdate: Function;
    onDismiss: Function;
    onDone: Function;
    onFilterToggle: Function;
    onMultipleFilterToggle: Function;
    priceFilterOptions?: {
        isFilterByTotalPriceEnabled?: boolean;
        lengthOfStay?: number;
        showFilterByPriceType?: boolean;
    };
    priceInputValuesAreValid: boolean;
    results: number;
    selectedFilters: string[];
    showAllFiltersPill: boolean;
    showMoreFiltersPill: boolean;
    updatePriceInputValuesValidity: Function;
};

function FiltersContainer(props: FiltersContainerProps) {
    const {
        filterCountDataProvider: FilterCountDataProvider,
        allFiltersButtonOnly,
        allFiltersButtonCustomTrigger,
        searchResultsLoading,
        ...filterBarProps
    } = props;
    return (
        <EdapProvider>
            <ErrorReportBoundary name={'Filters threw an error'}>
                <FilterCountDataProvider
                    searchResultsLoading={searchResultsLoading}
                >
                    {({filterGroups, resultsCount}) => {
                        const commonProps = {
                            ...filterBarProps,
                            filterGroups,
                            results: resultsCount,
                        };

                        if (allFiltersButtonOnly) {
                            return (
                                <MoreFiltersModal
                                    {...commonProps}
                                    id="all-filters"
                                    sliceAt={0}
                                    allFiltersButtonCustomTrigger={
                                        allFiltersButtonCustomTrigger
                                    }
                                />
                            );
                        }

                        return <FilterBar {...commonProps} />;
                    }}
                </FilterCountDataProvider>
            </ErrorReportBoundary>
        </EdapProvider>
    );
}

export default FiltersContainer;
